import type { Editor } from "@tiptap/core";
import type { CanvasLinkAttributes, CanvasLinkAttributeSpec, CanvasLinkOptions } from "./types";
import { mergeAttributes, Node, VueNodeViewRenderer } from "@tiptap/vue-3";
import { getDiagramObject } from "ls/features/disko/interop/dotnetExports/bridge";
import { getObjectPermalinkWithoutRouter } from "ls/features/disko/permalinks";
import { getShortcutInformation } from "../../extensions/ShortcutInformation";
import { preventNodeMarks } from "../../preventNodeMarks";
import CanvasLink from "./CanvasLink.vue";
import { linkMarkToCanvasLink } from "./linkMarkToCanvasLink";
import { CanvasLinkNodeName } from "./types";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    canvasLink: {
      /**
       * Set canvas link
       */
      setCanvasLink: (attributes: CanvasLinkAttributes) => ReturnType;
    };
  }
}

export default Node.create<CanvasLinkOptions>({
  name: CanvasLinkNodeName,
  marks: "",
  group: "inline",
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      objectId: {
        default: null,
        parseHTML: element => element.getAttribute("data-object-id"),
        renderHTML: attrs => {
          const nodeAttrs = attrs as CanvasLinkAttributes;
          return {
            "data-object-id": nodeAttrs.objectId,
          };
        },
      },
    } satisfies CanvasLinkAttributeSpec;
  },

  parseHTML() {
    return [{
      tag: `a[data-type="${this.name}"]`,
      // set priority higher than default 'link' mark
      priority: 51,
    }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as CanvasLinkAttributes;
    const url = getObjectUrl(this.editor, attrs.objectId);
    let objectName = url;
    try {
      const diagramObject = getDiagramObject(attrs.objectId);
      if (diagramObject) {
        objectName = diagramObject.name;
      }
    } catch {
      // error is already logged
    }

    return [
      "a",
      mergeAttributes({
        "data-type": this.name,
        "data-object-id": attrs.objectId,
        href: url.toString(),
      }, HTMLAttributes),
      objectName,
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(CanvasLink);
  },

  renderText({ node }) {
    const attrs = node.attrs as CanvasLinkAttributes;
    try {
      const diagramObject = getDiagramObject(attrs.objectId);
      if (diagramObject) {
        return diagramObject.name;
      }
    } catch {
      // error is already logged
    }
    return getObjectUrl(this.editor, attrs.objectId);
  },

  addProseMirrorPlugins() {
    return [
      linkMarkToCanvasLink({
        shortcut: () => getShortcutInformation(this.editor).Shortcut,
      }),

      preventNodeMarks({
        type: this.type,
      }),
    ];
  },

  addCommands() {
    return {
      setCanvasLink: attrs => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs,
        });
      },
    };
  },
});

function getObjectUrl(editor: Editor | undefined, objectId: string) {
  const shortcut = editor ? getShortcutInformation(editor).Shortcut : "";
  return getObjectPermalinkWithoutRouter(shortcut, objectId);
}
